/**
 * In-world speech bubbles, shared by the town and the floor. Player chat
 * also lives in the ChatPanel; enemy taunts use a separate store and stay
 * above their speaker only.
 *
 * Callers feed speech events and draw in the overlay pass, anchored to the
 * INTERPOLATED position (the bubble glides with
 * the sprite under it, never ahead of it). Wall-clock only — cosmetics never
 * touch the sim (AGENTS §6).
 */
import { playerKey } from "./interpolate.js";
import { tileToScreen, HALF_H } from "./iso.js";

/** Long enough to catch in peripheral vision, short enough that stale
 *  chatter never crowds the heads of a busy room. */
export const CHAT_BUBBLE_MS = 6_000;
export const CHAT_BUBBLE_FADE_MS = 800;
/** A bubble is a shout, not a paragraph: the panel holds the long line. */
export const BUBBLE_MAX_LINES = 3;

export interface ChatBubble {
  text: string;
  until: number;
}

/** Speaker id -> their current bubble. One per speaker: a new line replaces
 *  their previous bubble rather than stacking a column of text. */
export type ChatBubbleStore = Map<number, ChatBubble>;

/** Record a line. `now` is injectable so a test can age a bubble. */
export function noteChat(bubbles: ChatBubbleStore, id: number, text: string, now = performance.now(), durationMs = CHAT_BUBBLE_MS): void {
  bubbles.set(id, { text, until: now + durationMs });
}

/** Drop everything expired (called on teardown, not per frame). */
export function clearBubbles(bubbles: ChatBubbleStore): void {
  bubbles.clear();
}

export interface BubbleFrame {
  ctx: CanvasRenderingContext2D;
  /** Interpolated positions, keyed by the supplied speakerKey (player by default). */
  positions: Map<string, { x: number; y: number }>;
  camX: number;
  camY: number;
  isoUnit: number;
  cssW: number;
  cssH: number;
  now: number;
}

/**
 * Draw every live bubble. `headHeight` is the hero sprite's anchor height in
 * units (the gap the bubble sits above the name tag), and `onScreenOnly`
 * decides what happens to a speaker outside the viewport: the town draws
 * everyone (one screen, clamp into the canvas), the floor skips them (a
 * teammate in another room is in the party observation, and a bubble floating
 * over unrevealed floor would speak for someone you cannot see — their line
 * still lands in the log).
 */
export function drawChatBubbles(
  frame: BubbleFrame,
  bubbles: ChatBubbleStore,
  headHeight: number | ((id: number) => number),
  onScreenOnly: boolean,
  speakerKey: (id: number) => string = playerKey,
): void {
  const { ctx, isoUnit: u, cssW, cssH } = frame;
  for (const [id, bubble] of bubbles) {
    const remaining = bubble.until - frame.now;
    if (remaining <= 0) {
      bubbles.delete(id);
      continue;
    }
    const pos = frame.positions.get(speakerKey(id));
    if (!pos) continue;

    const t = tileToScreen(pos.x, pos.y, u, frame.camX, frame.camY);
    const anchorX = t.x;
    const height = typeof headHeight === "number" ? headHeight : headHeight(id);
    const nameY = t.y + HALF_H * u - height * u - 9 * u;
    const fontSize = Math.max(10, 12 * u);
    const lineH = fontSize * 1.28;
    const maxTextW = Math.min(230, cssW * 0.34);
    ctx.save();
    ctx.font = `600 ${fontSize}px ui-monospace`;
    const lines = wrapBubbleText(ctx, bubble.text, maxTextW);
    const textW = Math.max(...lines.map((line) => ctx.measureText(line).width));
    const padX = 9;
    const padY = 7;
    const w = textW + padX * 2;
    const h = lines.length * lineH + padY * 2;
    const cx = Math.max(8 + w / 2, Math.min(cssW - 8 - w / 2, anchorX));
    const bottom = nameY - 17 * u;
    const left = cx - w / 2;
    const top = bottom - h;
    if (onScreenOnly && (anchorX < 0 || anchorX > cssW || bottom < 0 || top > cssH)) {
      ctx.restore();
      continue;
    }
    ctx.globalAlpha = Math.min(1, remaining / CHAT_BUBBLE_FADE_MS);

    // Tail first, then the rounded body hides its top seam.
    const tailX = Math.max(left + 12, Math.min(left + w - 12, anchorX));
    ctx.beginPath();
    ctx.moveTo(tailX - 6, bottom - 1);
    ctx.lineTo(tailX + 6, bottom - 1);
    ctx.lineTo(anchorX, bottom + 8);
    ctx.closePath();
    ctx.fillStyle = "rgba(15, 18, 24, 0.94)";
    ctx.fill();
    ctx.strokeStyle = "rgba(200, 162, 74, 0.78)";
    ctx.lineWidth = 1;
    ctx.stroke();

    ctx.beginPath();
    ctx.roundRect(left, top, w, h, 6);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = "#f1eee6";
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    for (let i = 0; i < lines.length; i++) {
      ctx.fillText(lines[i]!, cx, top + padY + i * lineH);
    }
    ctx.restore();
  }
}

/** Pixel-width wrapping with a hard split for long unbroken tokens. */
export function wrapBubbleText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const lines: string[] = [];
  let line = "";
  for (const word of text.trim().split(/\s+/)) {
    const candidate = line ? `${line} ${word}` : word;
    if (ctx.measureText(candidate).width <= maxWidth) {
      line = candidate;
      continue;
    }
    if (line) lines.push(line);
    line = "";
    let chunk = "";
    for (const char of word) {
      if (chunk && ctx.measureText(chunk + char).width > maxWidth) {
        lines.push(chunk);
        chunk = char;
      } else {
        chunk += char;
      }
    }
    line = chunk;
  }
  if (line) lines.push(line);
  return lines.length > 0 ? lines.slice(0, BUBBLE_MAX_LINES) : [""];
}
